import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, Edit2, Trash2, X, Check, AtSign } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { wsService } from '../services/WebSocketService';

interface Comment {
  commentId: string;
  commentText: string;
  authorEmail: string;
  createdAt: string;
  updatedAt: string;
  isAdminOnly?: boolean;
  needsReply?: boolean;        // admin -> partner
  partnerNeedsReply?: boolean; // partner -> admin
  reactions?: { [emoji: string]: string[] };
}

interface Mentionable {
  email: string;
  name?: string;
  role: 'admin' | 'partner';
}

const REACTION_EMOJIS = ['👀', '❓', '✅', '👍', '😊'];

interface CommentsSectionProps {
  submissionId: string;
  isAdminView?: boolean;
  adminOnly: boolean;
}

export function CommentsSection({ submissionId, isAdminView = false, adminOnly }: CommentsSectionProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  // @mention state
  const [mentionables, setMentionables] = useState<Mentionable[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAnchor, setMentionAnchor] = useState(0);
  const [mentionDropdownIdx, setMentionDropdownIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchComments();
    getCurrentUser();
    fetchMentionables();
  }, [submissionId, adminOnly]);

  useEffect(() => {
    const handleNotification = (notification: any) => {
      if (notification.type === 'jv_message_from_admin' ||
          notification.type === 'jv_message_from_user' ||
          notification.type === 'jv_admin_only_comment') {
        fetchComments();
      }
    };
    wsService.on('notification', handleNotification);
    return () => wsService.off('notification', handleNotification);
  }, [submissionId, adminOnly]);

  const getCurrentUser = async () => {
    try {
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload.email as string;
      setCurrentUserEmail(email || '');
    } catch (err) {
      console.error('Error getting current user:', err);
    }
  };

  const fetchMentionables = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      const res = await fetch(`${apiUrl}/jv-submissions/${submissionId}/mentionables`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMentionables(data.mentionables || []);
      }
    } catch { /* ignore — @mentions degrade gracefully */ }
  };

  const fetchComments = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      let endpoint;
      if (isAdminView && adminOnly) {
        endpoint = `${apiUrl}/admin/jv-submissions/${submissionId}/comments`;
      } else {
        endpoint = `${apiUrl}/jv-submissions/${submissionId}/comments`;
      }
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  // @mention textarea handler
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewComment(val);
    const cursor = e.target.selectionStart ?? val.length;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionAnchor(cursor - atMatch[0].length);
      setMentionDropdownIdx(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredMentionables = mentionQuery !== null
    ? mentionables.filter(m =>
        m.email.toLowerCase().includes(mentionQuery) ||
        (m.name || '').toLowerCase().includes(mentionQuery)
      )
    : [];

  const insertMention = (m: Mentionable) => {
    const label = m.name ? `${m.name} (${m.email})` : m.email;
    const before = newComment.slice(0, mentionAnchor);
    const after = newComment.slice(textareaRef.current?.selectionStart ?? newComment.length);
    const inserted = `@${label} `;
    setNewComment(before + inserted + after);
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && filteredMentionables.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionDropdownIdx(i => Math.min(i + 1, filteredMentionables.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionDropdownIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filteredMentionables[mentionDropdownIdx]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
  };

  // needsReply is admin->partner; partnerNeedsReply is the mirror, partner->admin.
  // The backend ignores whichever one the caller isn't entitled to set.
  const handleAddComment = async (needsReply: boolean, partnerNeedsReply = false) => {
    if (!newComment.trim()) return;
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      const endpoint = isAdminView && adminOnly
        ? `${apiUrl}/admin/jv-submissions/${submissionId}/comments`
        : `${apiUrl}/jv-submissions/${submissionId}/comments`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentText: newComment, needsReply, partnerNeedsReply }),
      });
      if (response.ok) {
        setNewComment('');
        setMentionQuery(null);
        await fetchComments();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!editText.trim()) return;
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/jv-submissions/${submissionId}/comments/${commentId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentText: editText }),
      });
      if (response.ok) {
        setEditingId(null);
        setEditText('');
        await fetchComments();
      }
    } catch (err) {
      console.error('Error updating comment:', err);
      alert('Failed to update comment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/admin/jv-submissions/${submissionId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setComments([]);
        await fetchComments();
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    } finally {
      setLoading(false);
    }
  };

  const handleReact = async (commentId: string, emoji: string) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) return;
      const apiUrl = import.meta.env.VITE_API_URL;
      const endpoint = isAdminView && adminOnly
        ? `${apiUrl}/admin/jv-submissions/${submissionId}/comments/${commentId}/react`
        : `${apiUrl}/jv-submissions/${submissionId}/comments/${commentId}/react`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
      if (response.ok) {
        const { reactions } = await response.json();
        setComments(prev => prev.map(c => c.commentId === commentId ? { ...c, reactions } : c));
      }
    } catch (err) {
      console.error('Error reacting to comment:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isAuthor = (comment: Comment) => comment.authorEmail === currentUserEmail;

  return (
    <div className="bg-gray-50 rounded-xl p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <MessageSquare className={`w-5 h-5 ${adminOnly ? 'text-purple-600' : 'text-tdb-accent'}`} />
          <h3 className="text-lg font-bold text-gray-900">
            {adminOnly ? 'Admin-Only Discussion' : 'User Communication'}
          </h3>
          <span className="text-sm text-gray-500">({comments.length})</span>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
        {comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.commentId}
              className={`bg-white rounded-lg p-4 shadow-sm ${
                comment.isAdminOnly ? 'border-l-4 border-purple-500' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                    <span className="font-semibold text-gray-900 text-sm">{comment.authorEmail}</span>
                    {comment.isAdminOnly && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded">Admin Only</span>
                    )}
                    {comment.partnerNeedsReply && (
                      <span title="Partner asked for a reply — the response clock is running" className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded">Reply Requested</span>
                    )}
                    {comment.needsReply && (
                      <span title="Admin requested a reply — partner SLA clock is running" className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded">Needs Reply</span>
                    )}
                    <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                    {comment.updatedAt !== comment.createdAt && (
                      <span className="text-xs text-gray-400 italic">(edited)</span>
                    )}
                  </div>

                  {editingId === comment.commentId ? (
                    <div className="mt-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent resize-none"
                        rows={3}
                      />
                      <div className="flex items-center space-x-2 mt-2">
                        <button onClick={() => handleUpdateComment(comment.commentId)} disabled={loading}
                          className="px-3 py-1 bg-tdb-accent text-white rounded-lg hover:bg-opacity-90 text-sm flex items-center space-x-1">
                          <Check className="w-4 h-4" /><span>Save</span>
                        </button>
                        <button onClick={() => { setEditingId(null); setEditText(''); }}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm flex items-center space-x-1">
                          <X className="w-4 h-4" /><span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.commentText}</p>
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        {REACTION_EMOJIS.map(emoji => {
                          const reactors = comment.reactions?.[emoji] ?? [];
                          const hasReacted = reactors.includes(currentUserEmail);
                          return (
                            <button key={emoji} onClick={() => handleReact(comment.commentId, emoji)}
                              title={reactors.length > 0 ? reactors.join(', ') : `React with ${emoji}`}
                              className={`flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs border transition-colors ${
                                hasReacted ? 'bg-tdb-accent/10 border-tdb-accent text-tdb-accent' : 'bg-gray-100 border-transparent text-gray-500 hover:bg-gray-200'
                              }`}>
                              <span>{emoji}</span>
                              {reactors.length > 0 && <span className="font-medium ml-0.5">{reactors.length}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {isAuthor(comment) && editingId !== comment.commentId && (
                  <div className="flex items-center space-x-1 ml-2">
                    <button onClick={() => { setEditingId(comment.commentId); setEditText(comment.commentText); }}
                      className="p-1 text-gray-500 hover:text-tdb-accent transition-colors" title="Edit comment">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteComment(comment.commentId)} disabled={loading}
                      className="p-1 text-gray-500 hover:text-red-600 transition-colors" title="Delete comment">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <div className="mt-4 relative">
        {adminOnly && isAdminView && (
          <div className="mb-2 text-xs text-gray-600">
            <span className="font-semibold text-purple-600">Admin-Only Discussion (user cannot see these comments)</span>
          </div>
        )}

        {mentionables.length > 0 && (
          <div className="mb-1 flex items-center gap-1 text-xs text-gray-400">
            <AtSign className="w-3 h-3" />
            <span>Type @ to mention someone</span>
          </div>
        )}

        <div className="relative">
          <textarea
            ref={textareaRef}
            value={newComment}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={adminOnly ? 'Add admin-only comment (user will not see this)...' : 'Add a comment... (@ to mention)'}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 resize-none ${
              adminOnly
                ? 'border-purple-200 focus:ring-purple-500 focus:border-purple-500'
                : 'border-gray-200 focus:ring-tdb-accent focus:border-tdb-accent'
            }`}
            rows={3}
          />

          {/* @mention dropdown */}
          {mentionQuery !== null && filteredMentionables.length > 0 && (
            <div className="absolute z-50 left-0 bottom-full mb-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {filteredMentionables.map((m, idx) => (
                <button key={m.email} onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${idx === mentionDropdownIdx ? 'bg-gray-100' : ''}`}>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${m.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {m.role}
                  </span>
                  <div>
                    {m.name && <div className="text-sm font-medium text-gray-900">{m.name}</div>}
                    <div className="text-xs text-gray-500">{m.email}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* POST / Needs Reply buttons — only show for admins on the admin view */}
        {isAdminView ? (
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => handleAddComment(false)}
              disabled={loading || !newComment.trim()}
              className={`px-4 py-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-white ${
                adminOnly ? 'bg-purple-600' : 'bg-tdb-accent'
              }`}
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Posting...' : 'Post'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleAddComment(true)}
              disabled={loading || !newComment.trim()}
              title="Post and flag as Needs Reply — starts a 48h partner response SLA"
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-white"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Posting...' : 'Needs Reply'}</span>
            </button>
          </div>
        ) : (
          /* Partner view: plain post stays the primary action. "Request a Reply"
             is the mirror of the admin Needs Reply button — it is what starts
             the admin response clock. Without it an unflagged comment (a
             thank-you, an FYI) starts no clock at all. */
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => handleAddComment(false)}
              disabled={loading || !newComment.trim()}
              className="px-4 py-2 rounded-lg bg-tdb-accent hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-white"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Posting...' : 'Post Comment'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleAddComment(false, true)}
              disabled={loading || !newComment.trim()}
              title="Post and ask the team to reply — starts the response clock. Use this when you need an answer, not for FYIs or thank-yous."
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-white"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Posting...' : 'Request a Reply'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
